import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Fragment, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material';
import DatePicker from '../partials/pickers/DatePicker';
import ObjectPicker from '../partials/pickers/ObjectPicker';
import ObjectListPicker from '../partials/pickers/ObjectListPicker';
import ObjectSelectPicker from '../partials/pickers/ObjectSelectPicker';
import TimeListPicker from '../partials/pickers/TimeListPicker';
import TimeRoomPicker from '../partials/pickers/TimeRoomPicker';
import RoomPicker from '../partials/pickers/RoomPicker';
import TimePicker from '../partials/pickers/TimePicker';
import WeekPicker from '../partials/pickers/WeekPicker';
import SchedulePicker from '../partials/pickers/SchedulePicker';
import WeekNumberPicker from '../partials/pickers/WeekNumberPicker';
import ObjectGroupedListPicker from '../partials/pickers/ObjectGroupedListPicker';
import useWidgetStore from '../store/useWidgetStore';
import useDataStore from '../store/useDataStore';
import { FormContainer, useForm, useWatch } from 'react-hook-form-mui';
import { useI18N } from '../context/I18NContext';
import { useFlow } from '../context/FlowContext';
import { useSettings } from '../context/SettingsContext';
import CapacityPicker from '../partials/pickers/CapacityPicker';
import ErrorAlert from '../partials/ErrorAlert';
import useWarningStore from '../store/useWarningStore';
import ObjectGroupedCategoryPicker from '../partials/pickers/ObjectGroupedCategoryPicker';
import ObjectSelectAvatarPicker from '../partials/pickers/ObjectSelectAvatarPicker';
import { SingleOptionLabel } from '../layouts/Theme';
import { priceLabel } from '../helpers/general';
// the object selects that follow each other on a view that also picks a date
const CHAINED_SOURCES = ['group', 'apptype', 'resource'];
export default function Select(props) {
    var _a, _b, _c;
    const { setSelectedDate, setSelectedTime, setSelectedGroup, setSelectedGroups, setSelectedApptype, setSelectedApptypes, setSelectedResource, setSelectedResources, setSelectedCategory, setSelectedCategories, setSelectedProduct, setSelectedProducts, enabled, setEnabled, selectedApptype, selectedGroup, selectedResource } = useWidgetStore();
    const { orientation, widgetMode, displayRemainingCapacity, displayNoCapacity } = useSettings();
    const { groups, apptypes, resources, categories, products, dates, times, } = useDataStore();
    const flow = useFlow();
    const location = useLocation();
    // SlideRoutes keeps every view permanently mounted (just hidden) rather than unmounting it
    // once the visitor navigates away - captured once, on this instance's first render, since
    // that is the only point at which location reliably reflects the route this Select instance
    // was actually created for.
    const ownRoute = useRef(location.pathname.replace(/^\/oa_widget_?/, '').split('/')[0]).current;
    // Comparing against window.location.hash directly, not flow.currentRoute or useLocation():
    // SlideRoutes' ViewContext freezes BOTH of those for a view once it is left behind, at
    // whatever they were the moment it stopped being current - so a frozen ownRoute compared
    // against an equally-frozen flow.currentRoute always trivially matches, even long after this
    // instance is no longer the one on screen. The raw hash is the one thing here no React
    // context ever freezes. This is what lets this instance tell whether it is genuinely the one
    // on screen right now - a picker rendered by an instance that is not is must not react to the
    // store changing, see ObjectSelectPicker's `active` prop below.
    const currentHashRoute = window.location.hash.substring(12).split('/')[0];
    const isActiveView = currentHashRoute === ownRoute;
    // select1 is the only view with the apptype/group list, and every arrival on it - the
    // initial one, or coming back from any later step - has to start fresh, as if the widget
    // was never touched.
    //
    // Still needs the delay: the view being left stays mounted and reactive to the (global, not
    // flow-scoped) store indefinitely, not just for a transition window, so clearing the
    // selection while it's still on screen just feeds it an empty selection, which its own
    // picker immediately re-defaults - undoing the reset before the visitor even sees it.
    // SlideRoutes' default transition duration is 200ms; wait past that. (The `active` prop
    // passed to ObjectSelectPicker below stops that specific picker from doing this, but this
    // delay still protects against anything else reactive to the same fields.)
    useEffect(() => {
        var _a;
        if (((_a = flow.currentRoute) === null || _a === void 0 ? void 0 : _a.route) === 'select1') {
            const timer = setTimeout(() => {
                // top-down: clearing apptype or resource first would briefly leave group (or
                // apptype) still pointed at the old value, and a picker cross-validating
                // against that stale field could reselect what this just cleared
                setSelectedGroup();
                setSelectedApptype();
                setSelectedResource();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [flow.currentRoute]);
    const theme = useTheme();
    const { t, ts } = useI18N();
    const containerRef = useRef(null);
    const [showNext, setShowNext] = useState(false);
    const [selectionStr, setSelectionStr] = useState('');
    const warning = useWarningStore((state) => state.warning);
    const setWarning = useWarningStore((state) => state.setWarning);
    const [error, setError] = useState([]);
    useEffect(() => {
        setError(warning === '' ? [] : [warning]);
    }, [warning]);
    useEffect(() => {
        if ((error === null || error === void 0 ? void 0 : error.length) === 0) {
            setWarning('');
        }
    }, [error]);
    const formContext = useForm({});
    const group = useWatch({ control: formContext.control, name: "group" });
    const apptype = useWatch({ control: formContext.control, name: "apptype" });
    const resource = useWatch({ control: formContext.control, name: "resource" });
    const category = useWatch({ control: formContext.control, name: "category" });
    const product = useWatch({ control: formContext.control, name: "product" });
    const date = useWatch({ control: formContext.control, name: "date" });
    const time = useWatch({ control: formContext.control, name: "time" });
    useEffect(() => {
        if (formContext.getValues("group") !== undefined) {
            let selected = formContext.getValues("group");
            if (typeof selected === 'number') {
                setSelectedGroup(selected);
            }
            else {
                selected.length > 1 ? setSelectedGroups(selected) : setSelectedGroup(selected[0]);
            }
        }
    }, [group]);
    useEffect(() => {
        if (formContext.getValues("apptype") !== undefined) {
            let selected = formContext.getValues("apptype");
            if (typeof selected === 'number') {
                setSelectedApptype(selected);
            }
            else {
                selected.length > 1 ? setSelectedApptypes(selected) : setSelectedApptype(selected[0]);
            }
        }
    }, [apptype, apptypes]);
    useEffect(() => {
        if (formContext.getValues("resource") !== undefined) {
            let selected = formContext.getValues("resource");
            if (typeof selected === 'number') {
                setSelectedResource(selected);
            }
            else {
                selected.length > 1 ? setSelectedResources(selected) : setSelectedResource(selected[0]);
            }
        }
    }, [resource]);
    useEffect(() => {
        if (formContext.getValues("category") !== undefined) {
            let selected = formContext.getValues("category");
            if (typeof selected === 'number') {
                setSelectedCategory(selected);
            }
            else {
                selected.length > 1 ? setSelectedCategories(selected) : setSelectedCategory(selected[0]);
            }
        }
    }, [category]);
    useEffect(() => {
        if (formContext.getValues("product") !== undefined) {
            let selected = formContext.getValues("product");
            if (typeof selected === 'number') {
                setSelectedProduct(selected);
            }
            else {
                selected.length > 1 ? setSelectedProducts(selected) : setSelectedProduct(selected[0]);
            }
        }
    }, [product]);
    useEffect(() => {
        if (formContext.getValues("date") !== undefined) {
            setSelectedDate(formContext.getValues("date"));
        }
    }, [date]);
    useEffect(() => {
        if (formContext.getValues("time") !== undefined) {
            setSelectedTime(formContext.getValues("time"));
        }
    }, [time]);
    const onSubmit = (data, event) => {
        // form does nothing, is just here to provide a context for the pickers inside
    };
    const onError = (errors) => { };
    // Mirrors the dependenciesMet check ObjectSelectPicker makes for itself: a preceding
    // chained source only counts as resolved once something is actually selected for it.
    const getSelectedFor = (source) => {
        switch (source) {
            case 'group': return selectedGroup;
            case 'apptype': return selectedApptype;
            case 'resource': return selectedResource;
        }
    };
    const onNext = () => {
        setShowNext(false);
        if (flow.onNext) {
            flow.onNext();
        }
    };
    useEffect(() => {
        var _a, _b;
        (_b = (_a = flow.currentView) === null || _a === void 0 ? void 0 : _a.components) === null || _b === void 0 ? void 0 : _b.map((component, i) => {
            if (component.source) {
                switch (component.source) {
                    case "apptype":
                        if (!enabled.apptype) {
                            setEnabled(Object.assign(Object.assign({}, enabled), { apptype: true }));
                        }
                        ;
                        break;
                    case "resource":
                        if (!enabled.resource) {
                            setEnabled(Object.assign(Object.assign({}, enabled), { resource: true }));
                        }
                        ;
                        break;
                    case "group":
                        if (!enabled.group) {
                            setEnabled(Object.assign(Object.assign({}, enabled), { group: true }));
                        }
                        ;
                        break;
                    case "category":
                        if (!enabled.category) {
                            setEnabled(Object.assign(Object.assign({}, enabled), { category: true }));
                        }
                        ;
                        break;
                    case "product":
                        if (!enabled.product) {
                            setEnabled(Object.assign(Object.assign({}, enabled), { product: true }));
                        }
                        ;
                        break;
                    case "date":
                        if (!enabled.date) {
                            setEnabled(Object.assign(Object.assign({}, enabled), { date: true }));
                        }
                        ;
                        break;
                    case "time":
                        if (!enabled.time) {
                            setEnabled(Object.assign(Object.assign({}, enabled), { time: true }));
                        }
                        ;
                        break;
                }
            }
        });
    }, [
        flow.currentView,
        enabled
    ]);
    useEffect(() => {
        var _a, _b, _c, _d;
        if (((_a = flow.currentRoute) === null || _a === void 0 ? void 0 : _a.route) !== ((_b = flow.currentView) === null || _b === void 0 ? void 0 : _b.route.route)) {
            return; // we are in transition
        }
        (_d = (_c = flow.currentView) === null || _c === void 0 ? void 0 : _c.components) === null || _d === void 0 ? void 0 : _d.map((component, i) => {
            var _a, _b, _c, _d, _e, _f;
            if (component.source && component.picker) {
                let source = component.source;
                if (component.picker === 'objectGroupedList') {
                    // for the grouped list, we actually care about the inner source
                    source = source === 'group' ? 'apptype' : 'product';
                }
                switch (source) {
                    case 'apptype':
                        if (selectedApptype && selectedApptype.length > 0) { // && ![undefined, 0, -1].includes(selectedApptype[0])) {
                            let info = [];
                            selectedApptype.forEach((appointmentType_id) => {
                                if ([undefined, 0, -1].includes(appointmentType_id))
                                    return;
                                let appointmentType = apptypes.find((appointmentType) => appointmentType.id === appointmentType_id);
                                if (appointmentType) {
                                    info.push(appointmentType.name);
                                }
                            });
                            if (info.length > 0) {
                                setSelectionStr(info.join(', '));
                                setShowNext((_c = (_b = (_a = flow === null || flow === void 0 ? void 0 : flow.currentView) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.hasNext) !== null && _c !== void 0 ? _c : false);
                            }
                            else {
                                setShowNext(false);
                            }
                        }
                        else {
                            setShowNext(false);
                        }
                        break;
                    case 'resource':
                        // @todo, allow multi resource select?
                        if (selectedResource && selectedResource.length > 0 && selectedResource[0] !== undefined) { // && selectedResource[0] > 0 /* no 0 or -1 */) {
                            setShowNext((_f = (_e = (_d = flow === null || flow === void 0 ? void 0 : flow.currentView) === null || _d === void 0 ? void 0 : _d.config) === null || _e === void 0 ? void 0 : _e.hasNext) !== null && _f !== void 0 ? _f : false);
                        }
                        else {
                            setShowNext(false);
                        }
                        break;
                }
            }
        });
    }, [selectedApptype, selectedResource]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g;
        if (((_a = flow.currentRoute) === null || _a === void 0 ? void 0 : _a.route) !== ((_b = flow.currentView) === null || _b === void 0 ? void 0 : _b.route.route)) {
            return; // we are in transition
        }
        const hasApptypeSelect = (_d = (_c = flow.currentView) === null || _c === void 0 ? void 0 : _c.components) === null || _d === void 0 ? void 0 : _d.some((component) => {
            return component.picker === 'objectGroupedList' ? component.source === 'group' : component.source === 'apptype';
        });
        if (hasApptypeSelect && selectedApptype && selectedApptype.length > 0) { // && ![undefined, 0, -1].includes(selectedApptype[0])) {
            let info = [];
            selectedApptype.forEach((appointmentType_id) => {
                if ([undefined, 0, -1].includes(appointmentType_id))
                    return;
                let appointmentType = apptypes.find((appointmentType) => appointmentType.id === appointmentType_id);
                if (appointmentType) {
                    info.push(appointmentType.name);
                }
            });
            if (info.length > 0) {
                setSelectionStr(info.join(', '));
            }
            setShowNext((_g = (_f = (_e = flow === null || flow === void 0 ? void 0 : flow.currentView) === null || _e === void 0 ? void 0 : _e.config) === null || _f === void 0 ? void 0 : _f.hasNext) !== null && _g !== void 0 ? _g : false);
        }
    }, [
        flow.currentRoute,
        flow.currentView
    ]);
    const renderComponents = useMemo(() => {
        var _a, _b, _c, _d, _e, _f;
        if (((_a = flow.currentRoute) === null || _a === void 0 ? void 0 : _a.route) !== ((_b = flow.currentView) === null || _b === void 0 ? void 0 : _b.route.route)) {
            return (_jsx(Box, { sx: {
                    width: '100%',
                    textAlign: 'center',
                    paddingTop: '50%'
                }, children: _jsx(CircularProgress, {}) })); // we are in transition
        }
        let dataLoading = 0, // this is number, not a boolean, for debug purposes
        requiredSources = [];
        // first, collect an array with the required sources for the current view
        (_d = (_c = flow.currentView) === null || _c === void 0 ? void 0 : _c.components) === null || _d === void 0 ? void 0 : _d.map((component, i) => {
            if (component.source) {
                requiredSources.push(component.source);
            }
        });
        // a view that picks a date is the one where the object selects form a chain
        let hasDateSelect = requiredSources.includes('date');
        // then check only those source if are enabled, and finished loading
        if (requiredSources.includes('apptype') && enabled.apptype && apptypes.length === 0)
            dataLoading = 1;
        // resources are either loaded, or if we have no dates available, isn't relevant
        if (requiredSources.includes('resource') && enabled.resource && resources.length === 0 && (!enabled.date || dates.length > 0))
            dataLoading = 2;
        if (requiredSources.includes('group') && enabled.group && groups.length === 0)
            dataLoading = 3;
        if (requiredSources.includes('category') && enabled.category && categories.length === 0)
            dataLoading = 4;
        if (requiredSources.includes('product') && enabled.product && products.length === 0)
            dataLoading = 5;
        // dates can actually have no results to show
        // if (requiredSources.includes('date') && enabled.date && dates.length === 0) dataLoading = 6
        // times is always dependend on date, and we cant preload them before the datepicker decided the first date
        // if (requiredSources.includes('time') && enabled.time && times.length === 0) dataLoading = 7
        if (dataLoading > 0) {
            return (_jsx(Box, { sx: {
                    width: '100%',
                    textAlign: 'center',
                    paddingTop: '50%'
                }, children: _jsx(CircularProgress, {}) })); // we are still loading all dependencies
        }
        return (_jsx(_Fragment, { children: (_f = (_e = flow.currentView) === null || _e === void 0 ? void 0 : _e.components) === null || _f === void 0 ? void 0 : _f.map((component, i) => {
                var _a, _b, _c, _d, _e, _f, _g, _h;
                let _data = [];
                if (component.source) {
                    switch (component.source) {
                        case "apptype":
                            if (enabled.apptype) {
                                _data = apptypes;
                                if (selectedResource && selectedResource[0]) {
                                    // special case, we need to filter the apptypes clientside if there already is a resource selection
                                }
                                // A chosen category narrows the apptypes to that category.
                                // getApptypes answers for the whole widget and takes no group
                                // filter, but every apptype carries the group it belongs to
                                // (appTypeGroups -> groupId -> parentId), so the filtering
                                // happens here. -1 is "no preference" and narrows nothing.
                                // Only for the select pickers, and only on a view that has a group
                                // select to narrow with. Picking an apptype also sets selectedGroup
                                // (ObjectListPicker), and a list picker like select1's must never
                                // shrink to that apptype's category because of it.
                                let isSelectPicker = component.picker === 'objectSelect' || component.picker === 'objectSelectAvatar';
                                let hasGroupSelect = !!((_b = (_a = flow.currentView) === null || _a === void 0 ? void 0 : _a.components) === null || _b === void 0 ? void 0 : _b.some((c) => c.source === 'group'));
                                let _groups = (selectedGroup !== null && selectedGroup !== void 0 ? selectedGroup : []).filter((id) => id > 0);
                                if (isSelectPicker && hasGroupSelect && _groups.length > 0) {
                                    _data = _data.filter((apptype) => apptype.parentId !== undefined && _groups.includes(apptype.parentId));
                                }
                            }
                            ;
                            break;
                        case "resource":
                            if (enabled.resource) {
                                _data = resources;
                            }
                            ;
                            break;
                        case "group":
                            if (enabled.group) {
                                _data = groups;
                                // a category with no apptypes has nothing to book: picking it
                                // leaves the apptype select empty and the rest of the chain stuck,
                                // so it is not offered at all
                                if (apptypes.length > 0) {
                                    _data = _data.filter((group) => apptypes.some((apptype) => apptype.parentId === group.id));
                                }
                            }
                            ;
                            break;
                        case "category":
                            if (enabled.category) {
                                _data = categories;
                            }
                            ;
                            break;
                        case "product":
                            if (enabled.product) {
                                _data = products;
                            }
                            ;
                            break;
                        case "date":
                            if (enabled.date) {
                                _data = dates;
                            }
                            ;
                            break;
                        case "time":
                            if (enabled.time) {
                                _data = times;
                            }
                            ;
                            break;
                    }
                }
                let picker = _jsx(_Fragment, {});
                // On a view that also picks a date, the group/apptype/resource selects form a
                // chain: each one follows the one above it. Until every select above it has a
                // value it stays blank and disabled - a resource select configured with
                // defaultValue must not preselect while the apptype above it is still empty.
                // The first select is always enabled, and any other picker in between
                // (capacity, date, time) does not count.
                let precedingSources = [];
                if (hasDateSelect && CHAINED_SOURCES.includes(component.source)) {
                    precedingSources = ((_d = (_c = flow.currentView) === null || _c === void 0 ? void 0 : _c.components) !== null && _d !== void 0 ? _d : [])
                        .slice(0, i)
                        .filter((preceding) => `${preceding.picker}`.startsWith('object')
                        && CHAINED_SOURCES.includes(preceding.source))
                        .map((preceding) => preceding.source);
                }
                // An apptype select with only one option on offer renders as a hidden field
                // (see ObjectSelectPicker's onlyOption()) instead of a select the consumer
                // would open only to confirm the single thing on it - so the value is shown
                // as text instead. Only once the chain above it has actually resolved,
                // otherwise this would flash the wrong single option while still filtering.
                // 'group' collapses the same way in ObjectSelectPicker but draws no label
                // here - it stays fully silent when there is only one category on offer.
                let showSingleOptionLabel = false;
                if (component.source === 'apptype'
                    && (component.picker === 'objectSelect' || component.picker === 'objectSelectAvatar')
                    && precedingSources.every((source) => { var _a, _b; return ((_b = (_a = getSelectedFor(source)) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0; })
                    && _data.length === 1) {
                    showSingleOptionLabel = true;
                }
                let _config = (_e = component === null || component === void 0 ? void 0 : component.config) !== null && _e !== void 0 ? _e : {};
                if (selectedApptype && selectedApptype[0]) {
                    let apptype = apptypes.find((apptype) => apptype.id === selectedApptype[0]);
                    if (apptype) {
                        _config.min = apptype.minConsumers;
                        _config.max = apptype.maxConsumers;
                        _config.rules = apptype.capacityRules;
                    }
                }
                switch (component.picker) {
                    case "date":
                        picker = _jsx(DatePicker, { source: component.source, data: _data, config: _config });
                        break;
                    case "object":
                        picker = _jsx(ObjectPicker, { source: component.source, data: _data, config: component === null || component === void 0 ? void 0 : component.config });
                        break;
                    case "objectGroupedList":
                        picker = _jsx(ObjectGroupedListPicker, { source: component.source, data: _data, config: component === null || component === void 0 ? void 0 : component.config });
                        break;
                    case "objectGroupedCategory":
                        picker = _jsx(ObjectGroupedCategoryPicker, { source: component.source, data: _data, config: component === null || component === void 0 ? void 0 : component.config });
                        break;
                    case "objectList":
                        picker = _jsx(ObjectListPicker, { source: component.source, data: _data, config: component === null || component === void 0 ? void 0 : component.config });
                        break;
                    case "objectSelect":
                        picker = _jsx(ObjectSelectPicker, { source: component.source, data: _data, config: component === null || component === void 0 ? void 0 : component.config, dependsOn: precedingSources, active: isActiveView });
                        break;
                    case "objectSelectAvatar":
                        picker = _jsx(ObjectSelectAvatarPicker, { source: component.source, data: _data, config: component === null || component === void 0 ? void 0 : component.config, dependsOn: precedingSources });
                        break;
                    case "time":
                        picker = _jsx(TimePicker, { source: component.source, data: _data, config: _config });
                        break;
                    case "timeList":
                        picker = _jsx(TimeListPicker, { source: component.source, data: _data, config: _config });
                        break;
                    case "timeRoom":
                        picker = _jsx(TimeRoomPicker, { source: component.source, data: _data, config: component === null || component === void 0 ? void 0 : component.config });
                        break;
                    case "week":
                        // _config, not the raw one: the week picker gates on capacity the same way
                        // the date picker does, and min/max live on the enriched config
                        picker = _jsx(WeekPicker, { source: component.source, data: _data, config: _config });
                        break;
                    case "weekNumber":
                        picker = _jsx(WeekNumberPicker, { source: component.source, data: _data, config: component === null || component === void 0 ? void 0 : component.config });
                        break;
                    case "room":
                        picker = _jsx(RoomPicker, { source: component.source, data: _data, config: component === null || component === void 0 ? void 0 : component.config });
                        break;
                    case "schedule":
                        picker = _jsx(SchedulePicker, { source: component.source, data: _data, config: component === null || component === void 0 ? void 0 : component.config });
                        break;
                    case "capacity":
                        picker = _jsx(CapacityPicker, { source: component.source, data: _data, config: _config });
                        break;
                }
                return (_jsxs(Fragment, { children: [component.header && (_jsx(Typography, { variant: "subtitle1", sx: {
                                color: theme.palette.text.primary,
                                fontWeight: 700,
                                fontSize: '1.75rem',
                                my: 2,
                                paddingX: 2
                            }, children: t(component.header, 'select') })), !component.header && component.picker !== "objectGroupedList" && i === 0 && (_jsx(Box, { sx: {
                                height: '1em' // make sure the components isnt flush to the app title bar
                            } })), showSingleOptionLabel && (_jsxs(SingleOptionLabel, { children: [_jsx(Box, { component: "span", className: "single-option-label", children: t((_g = (_f = component === null || component === void 0 ? void 0 : component.config) === null || _f === void 0 ? void 0 : _f.label) !== null && _g !== void 0 ? _g : '') }), ((_h = component === null || component === void 0 ? void 0 : component.config) === null || _h === void 0 ? void 0 : _h.showPrice) && priceLabel(_data[0], { ts })
                                    ? `${_data[0].name} ( ${priceLabel(_data[0], { ts })} )`
                                    : _data[0].name] })), picker] }, i));
            }) }));
    }, [
        flow.currentView,
        flow.currentRoute,
        // selectedGroup / selectedResource narrow the apptype list above: without them the first
        // pick of a category left the list unfiltered until some other dependency changed
        enabled, apptypes, resources, groups, categories, products, dates, times,
        selectedGroup, selectedResource
    ]);
    return (_jsxs(FormContainer, { formContext: formContext, onSuccess: onSubmit, onError: onError, children: [_jsx(Box, { sx: {
                    paddingBottom: orientation === "horizontal" ? 0 : 2
                }, children: renderComponents }), _jsx(Box, { ref: containerRef, sx: Object.assign({ position: 'absolute', bottom: '2px', zIndex: 1000, width: '100%', borderRadius: '0 0 16px 16px', backgroundColor: 'transparent', overflow: 'hidden', paddingTop: '16px' }, (widgetMode === 'standalone' && {
                    maxWidth: '500px'
                })), children: _jsx(Slide, { direction: 'up', in: showNext, mountOnEnter: true, unmountOnExit: true, container: containerRef.current, children: _jsxs(Paper, { elevation: 4, sx: {
                            padding: 1,
                            backgroundColor: theme.palette.background.default,
                            borderTop: '#cecece 1px solid',
                            boxShadow: '0 -8px 16px 0 rgba(0,0,0,0.07)',
                        }, children: [error && error.length > 0 && (_jsx(ErrorAlert, { messages: error, setter: setError })), _jsxs(Stack, { direction: 'row', justifyContent: "space-between", alignItems: "center", children: [selectionStr !== '' && (_jsxs(Box, { sx: {
                                            width: '-webkit-fill-available'
                                        }, children: [_jsxs(Typography, { variant: "subtitle1", sx: {
                                                    color: theme.palette.text.primary,
                                                    fontWeight: 500,
                                                    fontSize: '0.9rem',
                                                    paddingX: 1,
                                                    lineHeight: '1.25rem',
                                                    marginBottom: '4px'
                                                }, children: [t('Jouw selectie', 'nav'), ":"] }), _jsx(Typography, { variant: "subtitle1", sx: {
                                                    color: theme.palette.text.primary,
                                                    fontWeight: 700,
                                                    fontSize: '1.25rem',
                                                    paddingX: 1,
                                                    lineHeight: '1.25rem'
                                                }, children: selectionStr })] })), _jsx(Box, { sx: Object.assign({}, (selectionStr === '' && {
                                            width: '100%'
                                        })), children: _jsx(Button, { onClick: onNext, sx: {
                                                my: 1,
                                                fontSize: '1rem',
                                                width: selectionStr === '' ? '100%' : 'max-content',
                                                marginRight: '12px',
                                                "&:hover": {
                                                    borderColor: theme.palette.primary.main
                                                }
                                            }, variant: "contained", children: ((_a = flow.currentRoute) === null || _a === void 0 ? void 0 : _a.actionLabel) ? t(((_c = (_b = flow.currentRoute) === null || _b === void 0 ? void 0 : _b.actionLabel) !== null && _c !== void 0 ? _c : 'Volgende')) : t('Volgende') }) })] })] }) }) })] }));
}
